class DynamicPopupModal extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    this.isKlaviyoPopup = this.getAttribute('isKlaviyoPopup') === 'true';
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: none; /* Start as display: none instead of using opacity */
          justify-content: center;
          align-items: center;
          z-index: 10000;
        }
        
        :host(.visible) {
          display: flex;
          animation: fadeIn 0.3s forwards;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        :host(.closing) {
          animation: fadeOut 0.3s forwards;
        }
        
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        .backdrop {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.7);
          cursor: pointer;
        }
        
        .modal {
          position: relative;
          background-color: white;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          max-width: 90%;
          max-height: 90%;
          overflow: auto;
          z-index: 1001;
          border-radius: 0; /* No border radius as requested */
          transform: translateY(0);
          animation: slideIn 0.3s forwards;
          width:680px;
        }
        
        @keyframes slideIn {
          from { transform: translateY(-20px); }
          to { transform: translateY(0); }
        }
        
        :host(.closing) .modal {
          animation: slideOut 0.3s forwards;
        }
        
        @keyframes slideOut {
          from { transform: translateY(0); }
          to { transform: translateY(-20px); }
        }
        
        .close-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 24px;
          height: 24px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1002;
        }
        
        .close-btn::before,
        .close-btn::after {
          content: '';
          position: absolute;
          width: 16px;
          height: 2px;
          background-color: #333;
        }
        
        .close-btn::before {
          transform: rotate(45deg);
        }
        
        .close-btn::after {
          transform: rotate(-45deg);
        }
        
        .content-container {
          padding: 20px;
          width:fit-content;
        }
        .content-container * {
          max-width: 100%;
        }

        /* Styles for third-party embeds (e.g. HubSpot) projected via named slot */
        ::slotted(div) {
          width: fit-content;
          max-width: 100%;
          width:100%;
        }

        /* For Klaviyo popups, hide the modal UI elements */
        :host([isKlaviyoPopup="true"]) .backdrop,
        :host([isKlaviyoPopup="true"]) .modal {
          display: none;
        }
        
        /* Special container for Klaviyo scripts that doesn't show as a modal */
        .klaviyo-container {
          position: static;
          /* Make sure this doesn't interfere with page layout */
          display: ${this.isKlaviyoPopup ? 'block' : 'none'};
          width: 0;
          height: 0;
          overflow: visible;
          z-index: -1;
        }
          @keyframes(max-width:767px) {
                    .modal { 
                      width:95%;
                    }
          }
      </style>
      <div class="backdrop"></div>
      <div class="modal">
        <button class="close-btn" aria-label="Close"></button>
        <div class="content-container"></div>
        <slot name="third-party-embed"></slot>
      </div>
      <div class="klaviyo-container"></div>
    `;
    
    this.backdrop = this.shadowRoot.querySelector('.backdrop');
    this.closeBtn = this.shadowRoot.querySelector('.close-btn');
    this.contentContainer = this.shadowRoot.querySelector('.content-container');
    this.klaviyoContainer = this.shadowRoot.querySelector('.klaviyo-container');
    
    this.close = this.close.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleEscKey = this.handleEscKey.bind(this);
    this.handleAnimationEnd = this.handleAnimationEnd.bind(this);
    
    this.backdrop.addEventListener('click', this.handleClick);
    this.closeBtn.addEventListener('click', this.handleClick);
    this.addEventListener('animationend', this.handleAnimationEnd);
    
    this.initialized = false;
    this.isOpening = false;
    this.pendingContent = null;
    
    const initialContent = this.getAttribute('content');
    if (initialContent) {
      this.pendingContent = initialContent;
    }

  }
  
  connectedCallback() {
    document.addEventListener('keydown', this.handleEscKey);
    

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeAfterDOMReady());
    } else {
      setTimeout(() => this.initializeAfterDOMReady(), 300);
    }
  }
  
  disconnectedCallback() {
    this.backdrop.removeEventListener('click', this.handleClick);
    this.closeBtn.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleEscKey);
    this.removeEventListener('animationend', this.handleAnimationEnd);
  }
  
  initializeAfterDOMReady() {
    this.isKlaviyoPopup = this.getAttribute('isKlaviyoPopup') === 'true';
    
    if (this.pendingContent && !this.initialized) {
      this.setContent(this.pendingContent);
      this.pendingContent = null;
      
      this.initialized = true;
      this.setAttribute('data-initialized', 'true');
      
      if (!this.isKlaviyoPopup) {
        setTimeout(() => {
          if (!this.isOpening && this.getAttribute('content')) {
            this.open();
          }
        }, 300);
      }
    }
  }
  
  static get observedAttributes() {
    return ['content', 'isKlaviyoPopup'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'isKlaviyoPopup' && oldValue !== newValue) {
      this.isKlaviyoPopup = newValue === 'true';
      if (this.klaviyoContainer) {
        this.klaviyoContainer.style.display = this.isKlaviyoPopup ? 'block' : 'none';
      }
    }
    
    if (name === 'content' && oldValue !== newValue && newValue) {
      if (!this.initialized) {
        this.pendingContent = newValue;
        return;
      }
      
      this.setContent(newValue);
    }
  }
  
  handleEscKey(e) {
    if (e.key === 'Escape' && this.classList.contains('visible')) {
      this.close();
    }
  }
  
  handleAnimationEnd(e) {
    if (this.classList.contains('closing')) {
      this.classList.remove('visible', 'closing');
      this.style.display = 'none';
    }
  }
  
  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.close();
  }
  
  decodeBase64Content(encodedContent) {
    if (!encodedContent) return '';
    
    try {
      if (encodedContent.match(/^[A-Za-z0-9+/=]+$/)) {
        return atob(encodedContent);
      }
      return encodedContent; 
    } catch (error) {
      console.error('Error decoding content:', error);
      return encodedContent; 
    }
  }
  
  decodeHTMLEntities(html) {
    if (!html) return '';
    
    const possiblyDecodedBase64 = this.decodeBase64Content(html);
    
    const txt = document.createElement('textarea');
    txt.innerHTML = possiblyDecodedBase64;
    return txt.value;
  }
  
  setContent(htmlContent) {
    if (!htmlContent) return;

    try {
      const decodedContent = this.decodeHTMLEntities(htmlContent);

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = decodedContent;

      // Detect third-party embeds that need light DOM access (e.g. HubSpot).
      // document.querySelectorAll() cannot pierce Shadow DOM, so the hs-form-frame
      // div must live as a light DOM child projected through a named slot.
      const hasEmbedForm = tempDiv.querySelector('.hs-form-frame, [data-form-id]');

      if (hasEmbedForm) {
        // Move external scripts to document.head, deduplicating by src
        Array.from(tempDiv.querySelectorAll('script[src]')).forEach(oldScript => {
          const src = oldScript.getAttribute('src');
          if (!document.querySelector(`script[src="${src}"]`)) {
            const newScript = document.createElement('script');
            Array.from(oldScript.attributes).forEach(attr => newScript.setAttribute(attr.name, attr.value));
            document.head.appendChild(newScript);
          }
          oldScript.remove();
        });

        // Clear any previously slotted embed content from prior opens
        Array.from(this.querySelectorAll('[slot="third-party-embed"]')).forEach(el => el.remove());

        // Wrap remaining content (including hs-form-frame) in a slotted div
        // so it renders inside the modal visually but lives in the light DOM
        const slotWrapper = document.createElement('div');
        slotWrapper.setAttribute('slot', 'third-party-embed');
        while (tempDiv.firstChild) {
          slotWrapper.appendChild(tempDiv.firstChild);
        }
        this.appendChild(slotWrapper);
        return;
      }

      // Regular (non-embed) content — existing behaviour
      const container = this.isKlaviyoPopup ? this.klaviyoContainer : this.contentContainer;

      container.innerHTML = '';

      while (tempDiv.firstChild) {
        container.appendChild(tempDiv.firstChild);
      }

      const scriptTags = Array.from(container.querySelectorAll('script'));

      scriptTags.forEach(oldScript => {
        const newScript = document.createElement('script');

        Array.from(oldScript.attributes).forEach(attr => {
          newScript.setAttribute(attr.name, attr.value);
        });

        newScript.textContent = oldScript.textContent;

        if (oldScript.parentNode) {
          oldScript.parentNode.replaceChild(newScript, oldScript);
        }
      });
    } catch (error) {
      console.error('Error setting content in dynamic-popup-modal:', error);
    }
  }
  
  open() {
    if (this.isKlaviyoPopup) {
      return;
    }
    
    if (this.classList.contains('visible') || this.isOpening) return;
    
    this.isOpening = true;
    
    this.classList.remove('closing');
    
    this.style.display = 'flex';
    
    requestAnimationFrame(() => {
      this.classList.add('visible');
      document.body.style.overflow = 'hidden'; 
      
      this.dispatchEvent(new CustomEvent('modal-open'));
      
      this.initialized = true;
      this.isOpening = false;
    });
  }
  
  close() {
    if (!this.classList.contains('visible')) return;
    
    this.classList.add('closing');
    document.body.style.overflow = ''; 
    
    this.dispatchEvent(new CustomEvent('modal-close'));
  }
  
  openWithContent(htmlContent) {
    if (!htmlContent) return;
    
    this.setContent(htmlContent);
    
    if (!this.isKlaviyoPopup) {
      setTimeout(() => {
        this.open();
      }, 50);
    }
  }
}

customElements.define('dynamic-popup-modal', DynamicPopupModal);

window.initDynamicModals = function(delay = 800) {
  return new Promise((resolve) => {
    const modals = document.querySelectorAll('dynamic-popup-modal');
    
    if (modals.length === 0) {
      resolve(false);
      return;
    }
    
    setTimeout(() => {
      let openedAny = false;
      
      modals.forEach(modal => {
        const isKlaviyoPopup = modal.getAttribute('isKlaviyoPopup') === 'true';
        
        if (!modal.hasAttribute('data-modal-initialized') && modal.getAttribute('content')) {
          modal.setAttribute('data-modal-initialized', 'true');
          
          if (!isKlaviyoPopup) {
            modal.open();
            openedAny = true;
          }
        }
      });
      
      resolve(openedAny);
    }, delay);
  });
};

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  setTimeout(() => {
    window.initDynamicModals();
  }, 1000);
} else {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      window.initDynamicModals();
    }, 1000);
  });
}